/**
 * The button.
 *
 * Two decisions here are worth stating, because both are places where the
 * obvious implementation is wrong.
 *
 * **A busy button stays focusable.** Setting `disabled` while a request is in
 * flight removes the element from the tab order, so the browser moves focus to
 * `<body>` and a keyboard or screen-reader user loses their place entirely —
 * mid-submit, with no announcement. Instead it carries `aria-disabled` and
 * swallows the click: still focusable, still announced, still unclickable.
 *
 * **The busy state is announced.** A spinner replacing a label is invisible to
 * anyone not looking at it, so `busyLabel` is rendered in a live region.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

import "./controls.css";

/**
 * `dark` is the third weight the design draws, between primary and secondary:
 * a real action that is not *the* action on the page. "Import a file" next to
 * "Publish" — both do something, only one is what the screen is for.
 */
export type ButtonVariant = "primary" | "secondary" | "dark" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** A request is in flight. Prefer this over `disabled` — see above. */
  busy?: boolean;
  /** Announced while busy. Required when `busy` can be true. */
  busyLabel?: string;
  /** Explicit, because a button inside a form defaults to submitting it. */
  type?: "button" | "submit" | "reset";
  children: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    busy = false,
    busyLabel,
    type = "button",
    disabled = false,
    className,
    onClick,
    children,
    ...rest
  },
  ref,
) {
  // `disabled` is a genuine "you may not do this"; `busy` is "not yet". Only
  // the former leaves the tab order.
  const inert = disabled || busy;

  return (
    <button
      ref={ref}
      type={type}
      className={["hardauto-button", className].filter(Boolean).join(" ")}
      data-variant={variant}
      data-size={size}
      data-busy={busy ? "true" : undefined}
      disabled={disabled}
      aria-disabled={inert || undefined}
      onClick={(event) => {
        // A busy button is still a real button to the browser, so the click
        // must be refused here rather than by the `disabled` attribute.
        if (inert) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...rest}
    >
      {busy ? <span className="hardauto-button__spinner" aria-hidden="true" /> : null}
      <span className="hardauto-button__label">{children}</span>
      {busy && busyLabel ? (
        <span role="status" aria-live="polite" className="hardauto-visually-hidden">
          {busyLabel}
        </span>
      ) : null}
    </button>
  );
});
